import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  BuildTemplate, CostCatalog, PlantSettings, SettingsTab, BackhaulSection, RadioEntry,
  CableSection, NetworkSection, OpticalSection, PlacementSection, RoutingSection, VaultsSection, DropsSection,
  SplicingSection, CrossingsSection,
  EconomicsSection,
} from '../types/specs';
import type { Plant, Project } from '../types/plant';

const TABS: { id: SettingsTab; label: string; ready: boolean }[] = [
  { id: 'network',        label: 'Network architecture', ready: true },
  { id: 'cable',          label: 'Cable specs',          ready: true },
  { id: 'placement',      label: 'Placement',            ready: true },
  { id: 'vaults',         label: 'Vaults & handholes',   ready: true },
  { id: 'splicing',       label: 'Splicing',             ready: true },
  { id: 'routing',        label: 'Route preferences',    ready: true },
  { id: 'crossings',      label: 'Crossings (rail/water)', ready: true },
  { id: 'optical',        label: 'Optical / passings',   ready: true },
  // 2026-05-31 hidden: pricing now lives in Rate Card only (/settings/catalog).
  // Tabs left commented for one release cycle; remove after #54 (DB col drop).
  // { id: 'materials_cost', label: 'Materials $',          ready: true },
  // { id: 'labor_cost',     label: 'Labor & adders $',     ready: true },
  { id: 'backhaul',       label: 'Backhaul & wireless',  ready: true },
  { id: 'drops',          label: 'Drops',                ready: true },
  { id: 'economics',      label: 'Economics',            ready: true },
];

export default function SettingsPage() {
  const qc = useQueryClient();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [tab, setTab] = useState<SettingsTab>('network');
  const [draft, setDraft] = useState<PlantSettings | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects'),
  });
  useEffect(() => { if (!selectedProjectId && projects[0]) setSelectedProjectId(projects[0].id); }, [projects, selectedProjectId]);

  const { data: plants = [] } = useQuery<Plant[]>({
    queryKey: ['plants', selectedProjectId],
    queryFn: () => selectedProjectId ? api.get(`/plants/project/${selectedProjectId}`) : Promise.resolve([]),
    enabled: !!selectedProjectId,
  });
  const plant = plants[0] ?? null;

  const { data: settings } = useQuery<PlantSettings>({
    queryKey: ['plant-settings', plant?.id],
    queryFn: () => plant ? api.get(`/settings/plant/${plant.id}`) : Promise.reject(),
    enabled: !!plant,
  });
  useEffect(() => { if (settings) setDraft(structuredClone(settings)); }, [settings]);

  const { data: templates = [] } = useQuery<BuildTemplate[]>({
    queryKey: ['build-templates'],
    queryFn: () => api.get('/settings/templates'),
  });
  const { data: catalogs = [] } = useQuery<CostCatalog[]>({
    queryKey: ['cost-catalogs'],
    queryFn: () => api.get('/settings/cost-catalogs'),
  });

  const saveMut = useMutation({
    mutationFn: (next: PlantSettings) =>
      api.put(`/settings/plant/${next.plant_id}`, {
        network: next.network, cable: next.cable, placement: next.placement,
        vaults: next.vaults, splicing: next.splicing, drops: next.drops,
        routing: next.routing, crossings: next.crossings, optical: next.optical,
        backhaul: next.backhaul,
        cost_catalog_id: next.cost_catalog_id,
      }),
    onSuccess: () => {
      setSavedAt(new Date().toLocaleTimeString());
      qc.invalidateQueries({ queryKey: ['plant-settings', plant?.id] });
    },
  });

  const applyTplMut = useMutation({
    mutationFn: (templateId: string) =>
      api.post(`/settings/templates/${templateId}/apply`, { plant_id: plant!.id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plant-settings', plant?.id] }),
  });

  if (!plant) return <div className="page__header"><div>Loading project + plant…</div></div>;
  if (!draft) return <div className="page__header"><div>Loading settings…</div></div>;

  function set<K extends keyof PlantSettings>(section: K, patch: Partial<PlantSettings[K]>) {
    setDraft((d) => d ? ({ ...d, [section]: { ...d[section] as any, ...patch } }) : d);
  }

  return (
    <div>
      <div className="page__header">
        <div>
          <h1 className="page__title">Plant settings</h1>
          <div className="page__subtitle">Every parameter the auto-design algorithm uses. Save and re-run a design to see the effect.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="map-select" value={selectedProjectId ?? ''}
                  onChange={(e) => setSelectedProjectId(e.target.value)}>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
          </select>
          <select className="map-select" value=""
                  onChange={(e) => e.target.value && applyTplMut.mutate(e.target.value)}>
            <option value="">Apply template…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button
            className="btn btn--primary"
            onClick={() => draft && saveMut.mutate(draft)}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? 'Saving…' : 'Save settings'}
          </button>
          {savedAt && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Saved {savedAt}</span>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16 }}>
        <div className="card">
          <div className="settings-rail">
            {TABS.filter((t) => t.ready).map((t) => (
              <button
                key={t.id}
                className={`settings-rail__btn ${tab === t.id ? 'active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                <span>{t.label}</span>
              </button>
            ))}

          </div>
        </div>

        <div className="card">
          <div className="card__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {tab === 'network'   && <NetworkForm  v={draft.network}   onChange={(p) => set('network', p)} />}
            {tab === 'cable'     && <CableForm    v={draft.cable}     onChange={(p) => set('cable', p)} />}
            {tab === 'placement' && <PlacementForm v={draft.placement} onChange={(p) => set('placement', p)} />}
            {tab === 'vaults'    && <VaultsForm   v={draft.vaults}    onChange={(p) => set('vaults', p)} />}
            {tab === 'splicing'  && <SplicingForm v={draft.splicing}  onChange={(p) => set('splicing', p)} />}
            {tab === 'routing'   && <RoutingForm  v={draft.routing}   onChange={(p) => set('routing', p)} />}
            {tab === 'crossings' && <CrossingsForm v={draft.crossings} onChange={(p) => set('crossings', p)} />}
            {tab === 'optical'   && <OpticalForm  v={draft.optical}   onChange={(p) => set('optical', p)} plantId={draft.plant_id} />}
            {tab === 'materials_cost' && <MaterialsCostForm catalogs={catalogs} plantId={plant.id} />}
            {tab === 'labor_cost'     && <LaborCostForm catalogs={catalogs} plantId={plant.id} />}
            {tab === 'backhaul'       && <BackhaulForm v={draft.backhaul ?? { radio_priority: 'throughput', lease_ptp_monthly: 1500, min_throughput_mbps: 100, max_hops: 5, tower_rental_per_ft_month: 1 }} onChange={(p) => set('backhaul', p)} />}
            {tab === 'drops'     && <DropsForm    v={draft.drops ?? { placement: 'buried', max_drop_ft: 500, mdu_treatment: 'single_drop_to_bef', drops_per_building: 1, generate_in_auto_design: true }} onChange={(p) => set('drops', p)} />}
            {tab === 'economics' && <EconomicsForm v={draft.economics ?? { ft_per_passing: 250, baseline_take_rate: 0.40, baseline_take_rate_unserved: 0.60, baseline_take_rate_served: 0.25, default_aerial_mix_pct: 100 }} onChange={(p) => set('economics', p)} />}

          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Section forms (5 wired this turn) -------------------------

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="settings-field">
      <label>
        <span className="settings-field__label">{label}</span>
        {hint && <span className="settings-field__hint">{hint}</span>}
      </label>
      <div className="settings-field__input">{children}</div>
    </div>
  );
}

function NetworkForm({ v, onChange }: { v: NetworkSection; onChange: (p: Partial<NetworkSection>) => void }) {
  return (<>
    <Field label="Splitter ratio" hint="Single-stage 1×N or cascaded 1×4→1×N">
      <select value={v.split_ratio} onChange={(e) => onChange({ split_ratio: e.target.value as any })}>
        {['1x4','1x8','1x16','1x32','1x64','1x4_1x8','1x4_1x16'].map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
    </Field>
    <Field label="Splitter placement" hint="Where the 1×N splitter sits in the plant">
      <select value={v.split_placement} onChange={(e) => onChange({ split_placement: e.target.value as any })}>
        <option value="centralized_olt">Centralized at OLT</option>
        <option value="centralized_cabinet">Centralized at cabinet</option>
        <option value="distributed_handhole">Distributed in handholes</option>
        <option value="distributed_pedestal">Distributed in pedestals</option>
        <option value="in_mdu">Inside MDU</option>
      </select>
    </Field>
    <Field label="Strands per home" hint="Active strands per BSL (usually 1; +1 for spare = 2)">
      <input type="number" min={0} max={24} value={v.strands_per_home} onChange={(e) => onChange({ strands_per_home: Number(e.target.value) })} />
    </Field>
    <Field label="Spare strand factor" hint="Multiplier on calculated strand count for spares (1.20 = +20%)">
      <input type="number" min={1} max={3} step={0.05} value={v.spare_strand_factor} onChange={(e) => onChange({ spare_strand_factor: Number(e.target.value) })} />
    </Field>
    <Field label="PON class" hint="Optical power budget class">
      <select value={v.pon_class} onChange={(e) => onChange({ pon_class: e.target.value as any })}>
        <option value="B+">B+ (28 dB)</option>
        <option value="C+">C+ (32 dB)</option>
        <option value="XGS-PON">XGS-PON (35 dB)</option>
      </select>
    </Field>
    <Field label="Max reach (m)" hint="Hard cap from OLT to the furthest ONT">
      <input type="number" min={1000} max={60000} value={v.max_reach_m} onChange={(e) => onChange({ max_reach_m: Number(e.target.value) })} />
    </Field>
  </>);
}

function CableForm({ v, onChange }: { v: CableSection; onChange: (p: Partial<CableSection>) => void }) {
  const STRAND_OPTS = [12,24,48,72,96,144,216,288,432,576,864];
  return (<>
    <h3 className="settings-h3">Strand counts</h3>
    <Field label="Feeder strand count">
      <select value={v.feeder_strands} onChange={(e) => onChange({ feeder_strands: Number(e.target.value) })}>
        {STRAND_OPTS.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </Field>
    <Field label="Feeder cable type">
      <input type="text" value={v.feeder_type} onChange={(e) => onChange({ feeder_type: e.target.value })} />
    </Field>
    <Field label="Distribution strand count">
      <select value={v.distribution_strands} onChange={(e) => onChange({ distribution_strands: Number(e.target.value) })}>
        {STRAND_OPTS.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </Field>
    <Field label="Distribution cable type">
      <input type="text" value={v.distribution_type} onChange={(e) => onChange({ distribution_type: e.target.value })} />
    </Field>
    <Field label="Drop strand count">
      <select value={v.drop_strands} onChange={(e) => onChange({ drop_strands: Number(e.target.value) })}>
        {[0,1,2,6,12,24,48,72].map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </Field>
    <Field label="Drop cable type">
      <input type="text" value={v.drop_type} onChange={(e) => onChange({ drop_type: e.target.value })} />
    </Field>

    <h3 className="settings-h3">Slack / coil lengths</h3>
    <Field label="Slack at splice closure (ft)" hint="Each side; standard is 75–100 ft">
      <input type="number" min={0} max={500} value={v.slack_at_closure_ft} onChange={(e) => onChange({ slack_at_closure_ft: Number(e.target.value) })} />
    </Field>
    <Field label="Slack at handhole (ft)">
      <input type="number" min={0} max={200} value={v.slack_at_handhole_ft} onChange={(e) => onChange({ slack_at_handhole_ft: Number(e.target.value) })} />
    </Field>
    <Field label="Slack at pedestal (ft)">
      <input type="number" min={0} max={200} value={v.slack_at_pedestal_ft} onChange={(e) => onChange({ slack_at_pedestal_ft: Number(e.target.value) })} />
    </Field>
    <Field label="Slack at building entrance (ft)">
      <input type="number" min={0} max={200} value={v.slack_at_building_ft} onChange={(e) => onChange({ slack_at_building_ft: Number(e.target.value) })} />
    </Field>
    <Field label="Slack at OLT / head-end (ft)">
      <input type="number" min={0} max={500} value={v.slack_at_olt_ft} onChange={(e) => onChange({ slack_at_olt_ft: Number(e.target.value) })} />
    </Field>
    <Field label="Aerial mid-span coil interval (ft)" hint="0 disables. Typical: every 500–1000 ft.">
      <input type="number" min={0} max={5000} value={v.aerial_midspan_coil_every_ft} onChange={(e) => onChange({ aerial_midspan_coil_every_ft: Number(e.target.value) })} />
    </Field>
    <Field label="Aerial mid-span coil length (ft)">
      <input type="number" min={0} max={200} value={v.aerial_midspan_coil_ft} onChange={(e) => onChange({ aerial_midspan_coil_ft: Number(e.target.value) })} />
    </Field>
  </>);
}

function PlacementForm({ v, onChange }: { v: PlacementSection; onChange: (p: Partial<PlacementSection>) => void }) {
  return (<>
    <Field label="Default placement" hint="Used for any segment not overridden by a route preference">
      <select value={v.default} onChange={(e) => onChange({ default: e.target.value as any })}>
        <option value="aerial">Aerial</option>
        <option value="buried">Buried</option>
        <option value="conduit">Conduit</option>
        <option value="direct_bury">Direct bury</option>
        <option value="micro_trench">Micro trench</option>
      </select>
    </Field>
    <Field label="Buried minimum depth (in)">
      <input type="number" min={12} max={72} value={v.buried_min_depth_in} onChange={(e) => onChange({ buried_min_depth_in: Number(e.target.value) })} />
    </Field>
    <Field label="Conduit count" hint="Number of conduits installed in trench (1 = single, 2 = duplicate spare)">
      <input type="number" min={0} max={6} value={v.conduit_count} onChange={(e) => onChange({ conduit_count: Number(e.target.value) })} />
    </Field>
    <Field label="Aerial lashing">
      <select value={v.aerial_lashing} onChange={(e) => onChange({ aerial_lashing: e.target.value as any })}>
        <option value="lashed">Lashed to messenger</option>
        <option value="self_supporting">Self-supporting (ADSS)</option>
        <option value="n/a">Not applicable</option>
      </select>
    </Field>
    <Field label="Trench restoration">
      <select value={v.trench_restoration} onChange={(e) => onChange({ trench_restoration: e.target.value as any })}>
        <option value="asphalt">Asphalt</option>
        <option value="concrete">Concrete</option>
        <option value="sod">Sod / dirt</option>
        <option value="gravel">Gravel</option>
        <option value="none">None</option>
      </select>
    </Field>

    <h3 className="settings-h3" style={{ marginTop: 18 }}>Soil-aware construction (SSURGO)</h3>
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
      Decision-tree thresholds + per-method labor rates that drive the Soil Profile card on Builder
      and Project Economics. Rates fall back to <code>cost_catalog.labor.per_ft</code> when blank.
      Defaults are NRCS / utility-construction handbook ranges.
    </div>

    <h4 style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', margin: '8px 0 4px' }}>Thresholds</h4>

    <Field label="Hydric % cutoff for directional bore"
           hint="Soils with hydric class ≥ this % route via directional bore (avoid wetland disturbance, may trigger §404 review). NRCS default: 50.">
      <input type="number" min={0} max={100} step={1}
        value={(v as any).soil_thresholds?.hydric_pct_wetland ?? 50}
        onChange={(e) => onChange({ soil_thresholds: { ...((v as any).soil_thresholds ?? {}), hydric_pct_wetland: Number(e.target.value) } } as any)} />
    </Field>

    <Field label="Bedrock depth cutoff for rock saw (in)"
           hint="Bedrock shallower than this depth triggers rock saw (vs plow/trench). Industry standard: 18 in.">
      <input type="number" min={0} max={120} step={1}
        value={(v as any).soil_thresholds?.bedrock_in_rock ?? 18}
        onChange={(e) => onChange({ soil_thresholds: { ...((v as any).soil_thresholds ?? {}), bedrock_in_rock: Number(e.target.value) } } as any)} />
    </Field>

    <Field label="Bedrock depth required for plow eligibility (in)"
           hint="Plowing needs at least this much soil depth before bedrock. Industry standard: 36 in. Tighter cutoffs reduce plow %.">
      <input type="number" min={0} max={120} step={1}
        value={(v as any).soil_thresholds?.bedrock_in_plow ?? 36}
        onChange={(e) => onChange({ soil_thresholds: { ...((v as any).soil_thresholds ?? {}), bedrock_in_plow: Number(e.target.value) } } as any)} />
    </Field>

    <Field label="Slope cutoff for trench-with-dewater (%)"
           hint="Slope steeper than this needs more equipment than plow can handle. Industry: 15%.">
      <input type="number" min={0} max={45} step={1}
        value={(v as any).soil_thresholds?.slope_pct_steep ?? 15}
        onChange={(e) => onChange({ soil_thresholds: { ...((v as any).soil_thresholds ?? {}), slope_pct_steep: Number(e.target.value) } } as any)} />
    </Field>

    <h4 style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', margin: '12px 0 4px' }}>
      Per-method labor rates ($/ft)
      <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 400, color: '#94a3b8' }}>blank = use cost catalog</span>
    </h4>

    <Field label="Aerial install (lashed/messengered)"
           hint="Stringing labor on existing pole strand. Industry typical: $3-7/ft all-in incl. attachment fees and hardware. Drives the aerial side of the aerial/buried mix slider.">
      <input type="number" min={0} max={500} step={0.10}
        value={(v as any).soil_rates?.aerial ?? ''}
        placeholder="(catalog)"
        onChange={(e) => onChange({ soil_rates: { ...((v as any).soil_rates ?? {}), aerial: e.target.value === '' ? undefined : Number(e.target.value) } } as any)} />
    </Field>

    <Field label="Plow / vibratory plow"
           hint="Cheapest buried method when conditions allow. Industry: $2-6/ft labor; $5-9/ft all-in incl. minimal restoration. Catalog default applies when blank.">
      <input type="number" min={0} max={500} step={0.10}
        value={(v as any).soil_rates?.plow ?? ''}
        placeholder="(catalog)"
        onChange={(e) => onChange({ soil_rates: { ...((v as any).soil_rates ?? {}), plow: e.target.value === '' ? undefined : Number(e.target.value) } } as any)} />
    </Field>

    <Field label="Trench (with dewater)"
           hint="Mid-tier; for steep slope or poor drainage.">
      <input type="number" min={0} max={500} step={0.10}
        value={(v as any).soil_rates?.trench ?? ''}
        placeholder="(catalog)"
        onChange={(e) => onChange({ soil_rates: { ...((v as any).soil_rates ?? {}), trench: e.target.value === '' ? undefined : Number(e.target.value) } } as any)} />
    </Field>

    <Field label="Rock saw / rock trencher"
           hint="For shallow bedrock. Significantly slower than plow.">
      <input type="number" min={0} max={500} step={0.10}
        value={(v as any).soil_rates?.rock_saw ?? ''}
        placeholder="(catalog)"
        onChange={(e) => onChange({ soil_rates: { ...((v as any).soil_rates ?? {}), rock_saw: e.target.value === '' ? undefined : Number(e.target.value) } } as any)} />
    </Field>

    <Field label="Directional bore (HDD)"
           hint="For wetlands, road crossings, sensitive areas. Industry: $20–100/ft depending on conditions.">
      <input type="number" min={0} max={500} step={0.10}
        value={(v as any).soil_rates?.directional_bore ?? ''}
        placeholder="(catalog)"
        onChange={(e) => onChange({ soil_rates: { ...((v as any).soil_rates ?? {}), directional_bore: e.target.value === '' ? undefined : Number(e.target.value) } } as any)} />
    </Field>
  </>);
}

function VaultsForm({ v, onChange }: { v: VaultsSection; onChange: (p: Partial<VaultsSection>) => void }) {
  return (<>
    <Field label="Feeder vault spacing (ft)" hint="Drop a vault every N feet along the feeder">
      <input type="number" min={100} max={5000} value={v.feeder_spacing_ft} onChange={(e) => onChange({ feeder_spacing_ft: Number(e.target.value) })} />
    </Field>
    <Field label="Distribution vault spacing (ft)">
      <input type="number" min={100} max={2000} value={v.distribution_spacing_ft} onChange={(e) => onChange({ distribution_spacing_ft: Number(e.target.value) })} />
    </Field>
    <Field label="Place at intersections" hint="Auto-add a handhole at every street intersection">
      <input type="checkbox" checked={v.place_at_intersections} onChange={(e) => onChange({ place_at_intersections: e.target.checked })} />
    </Field>
    <Field label="Default vault size">
      <select value={v.default_vault_size} onChange={(e) => onChange({ default_vault_size: e.target.value as any })}>
        <option value="30x48">30×48</option>
        <option value="36x60">36×60</option>
        <option value="48x60">48×60</option>
        <option value="60x60">60×60</option>
      </select>
    </Field>
    <Field label="Vault material">
      <select value={v.vault_material} onChange={(e) => onChange({ vault_material: e.target.value as any })}>
        <option value="polymer_concrete">Polymer concrete</option>
        <option value="fiberglass">Fiberglass</option>
        <option value="precast_concrete">Precast concrete</option>
      </select>
    </Field>
    <Field label="Traffic rating">
      <select value={v.vault_traffic_rating} onChange={(e) => onChange({ vault_traffic_rating: e.target.value as any })}>
        <option value="H-10">H-10 (light vehicle)</option>
        <option value="H-20">H-20 (heavy / roadway)</option>
      </select>
    </Field>
    <Field label="Use pedestals for drops" hint="If checked, drop terminals sit on pedestals; otherwise in handholes">
      <input type="checkbox" checked={v.use_pedestals_for_drops} onChange={(e) => onChange({ use_pedestals_for_drops: e.target.checked })} />
    </Field>
  </>);
}

function OpticalForm({ v, onChange, plantId }: { v: OpticalSection; onChange: (p: Partial<OpticalSection>) => void; plantId: string }) {
  const [clearText, setClearText] = useState('');
  const [clearing, setClearing] = useState(false);
  const [clearMsg, setClearMsg] = useState<string | null>(null);
  const REQUIRED = 'CLEAR';

  async function handleClear() {
    if (clearText !== REQUIRED) return;
    setClearing(true);
    setClearMsg(null);
    try {
      await api.post(`/plants/${plantId}/clear`, {});
      setClearMsg('Plant cleared.');
      setClearText('');
    } catch (e) {
      setClearMsg('Failed: ' + (e as Error).message);
    } finally {
      setClearing(false);
    }
  }

  return (<>
    <h3 className="settings-h3">Optical power budget</h3>
    <Field label="Budget class" hint="GPON Class B+ / C+ / XGS-PON">
      <select value={v.budget_class} onChange={(e) => onChange({ budget_class: e.target.value as any })}>
        <option value="B+">B+ (28 dB)</option>
        <option value="C+">C+ (32 dB)</option>
        <option value="XGS-PON">XGS-PON (35 dB)</option>
      </select>
    </Field>
    <Field label="Budget (dB)">
      <input type="number" min={20} max={50} step={0.5} value={v.budget_db} onChange={(e) => onChange({ budget_db: Number(e.target.value) })} />
    </Field>
    <Field label="Fiber loss per km (dB)">
      <input type="number" min={0.1} max={1.0} step={0.01} value={v.fiber_loss_per_km_db} onChange={(e) => onChange({ fiber_loss_per_km_db: Number(e.target.value) })} />
    </Field>
    <Field label="Headroom (dB)" hint="Safety margin — flag BSLs with less than this much reserve">
      <input type="number" min={0} max={10} step={0.5} value={v.headroom_db} onChange={(e) => onChange({ headroom_db: Number(e.target.value) })} />
    </Field>

    <h3 className="settings-h3" style={{ marginTop: 18 }}>Splitter loss (dB)</h3>
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
      Insertion loss per split ratio. Pulled into the optical budget calc when an
      auto-designed PON path picks the matching split. Typical values shown as defaults.
    </div>
    {(['1x4', '1x8', '1x16', '1x32', '1x64'] as const).map((ratio) => {
      const defaults: Record<string, number> = { '1x4': 7.2, '1x8': 10.5, '1x16': 14.0, '1x32': 17.5, '1x64': 21.0 };
      return (
        <Field key={ratio} label={`${ratio} splitter (dB)`} hint={`Industry typical: ${defaults[ratio]} dB`}>
          <input type="number" min={0} max={30} step={0.1}
            value={v.splitter_loss_db?.[ratio] ?? ''}
            placeholder={`(default ${defaults[ratio]})`}
            onChange={(e) => onChange({
              splitter_loss_db: {
                ...(v.splitter_loss_db ?? {}),
                [ratio]: e.target.value === '' ? undefined as any : Number(e.target.value),
              }
            })} />
        </Field>
      );
    })}

    <h3 className="settings-h3">Passings (SNDash sync)</h3>
    <Field label="Passing buffer (ft)" hint="A BSL this close to any lit segment counts as passed. Default 300 ft.">
      <input type="number" min={50} max={2000} step={10} value={v.passing_buffer_ft ?? 300} onChange={(e) => onChange({ passing_buffer_ft: Number(e.target.value) })} />
    </Field>

    <h3 className="settings-h3" style={{ color: 'var(--danger)', marginTop: 32 }}>Danger zone</h3>
    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12 }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: '#991b1b', marginBottom: 4 }}>Clear plant</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
        Deletes every node, segment, and drop in this plant. <b>Does NOT delete lit rows from GeoHub / SNDash</b> — unlight segments first if you want SNDash coverage removed. Type <code>{REQUIRED}</code> to enable.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          value={clearText}
          onChange={(e) => setClearText(e.target.value)}
          placeholder={`Type ${REQUIRED} to enable`}
          style={{ padding: 5, border: '1px solid var(--border-strong)', borderRadius: 4, fontSize: 13 }}
        />
        <button
          className="btn"
          onClick={handleClear}
          disabled={clearText !== REQUIRED || clearing}
          style={{ background: clearText === REQUIRED ? '#dc2626' : '#fecaca', color: 'white', borderColor: clearText === REQUIRED ? '#991b1b' : '#fca5a5' }}
        >
          {clearing ? 'Clearing…' : '🗑 Clear plant'}
        </button>
      </div>
      {clearMsg && <div style={{ marginTop: 6, fontSize: 12 }}>{clearMsg}</div>}
    </div>
  </>);
}

function RoutingForm({ v, onChange }: { v: RoutingSection; onChange: (p: Partial<RoutingSection>) => void }) {
  const POL = (
    <>
      <option value="off">Off (no preference)</option>
      <option value="soft">Soft (cost penalty)</option>
      <option value="hard">Hard (avoid entirely)</option>
    </>
  );
  return (<>
    <h3 className="settings-h3">Avoid policies</h3>
    <Field label="Railroads"><select value={v.avoid_railroads} onChange={(e) => onChange({ avoid_railroads: e.target.value as any })}>{POL}</select></Field>
    <Field label="Waterways"><select value={v.avoid_waterways} onChange={(e) => onChange({ avoid_waterways: e.target.value as any })}>{POL}</select></Field>
    <Field label="Interstates"><select value={v.avoid_interstates} onChange={(e) => onChange({ avoid_interstates: e.target.value as any })}>{POL}</select></Field>
    <Field label="US highways"><select value={v.avoid_us_highways} onChange={(e) => onChange({ avoid_us_highways: e.target.value as any })}>{POL}</select></Field>
    <Field label="Wetlands"><select value={v.avoid_wetlands} onChange={(e) => onChange({ avoid_wetlands: e.target.value as any })}>{POL}</select></Field>

    <h3 className="settings-h3">Penalty multipliers (soft policy)</h3>
    <Field label="Railroad penalty ×"><input type="number" min={1} max={50} value={v.rail_penalty_mult} onChange={(e) => onChange({ rail_penalty_mult: Number(e.target.value) })} /></Field>
    <Field label="Waterway penalty ×"><input type="number" min={1} max={50} value={v.water_penalty_mult} onChange={(e) => onChange({ water_penalty_mult: Number(e.target.value) })} /></Field>
    <Field label="Interstate penalty ×"><input type="number" min={1} max={50} value={v.interstate_penalty_mult} onChange={(e) => onChange({ interstate_penalty_mult: Number(e.target.value) })} /></Field>

    <h3 className="settings-h3">Geometry</h3>
    <Field label="Corridor padding (ft)" hint="How far outside the polygon to pull roads from">
      <input type="number" min={0} max={2000} value={v.corridor_padding_ft} onChange={(e) => onChange({ corridor_padding_ft: Number(e.target.value) })} />
    </Field>
    <Field label="Max slope %"><input type="number" min={0} max={100} value={v.max_slope_pct} onChange={(e) => onChange({ max_slope_pct: Number(e.target.value) })} /></Field>
  </>);
}

// ---------- Cost catalog editors -------------------------------------

function useCatalogEditor(catalogs: CostCatalog[], plantId: string) {
  const qc = useQueryClient();
  const cat = catalogs.find((c) => c.is_default) ?? catalogs[0] ?? null;
  const [draft, setDraft] = useState<CostCatalog | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  useEffect(() => { if (cat) setDraft(structuredClone(cat)); }, [cat?.id, cat?.updated_at]);

  const saveMut = useMutation({
    mutationFn: (next: CostCatalog) => api.put(`/settings/cost-catalogs/${next.id}`, {
      region_mult: Number(next.region_mult),
      materials: next.materials,
      labor: next.labor,
    }),
    onSuccess: () => {
      setSavedAt(new Date().toLocaleTimeString());
      qc.invalidateQueries({ queryKey: ['cost-catalogs'] });
      qc.invalidateQueries({ queryKey: ['bom', plantId] });
      qc.invalidateQueries({ queryKey: ['bom'] });
    },
  });

  return { cat, draft, setDraft, savedAt, saveMut };
}

function CatalogHeader({
  draft, savedAt, onSave, pending, onChangeRegion,
}: {
  draft: CostCatalog; savedAt: string | null; onSave: () => void; pending: boolean;
  onChangeRegion: (v: number) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{draft.name}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Edits update the BOM immediately after save. Default catalog applies to every plant unless overridden.
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Region ×</label>
        <input
          type="number" min={0.5} max={2.5} step={0.05}
          value={draft.region_mult}
          onChange={(e) => onChangeRegion(Number(e.target.value))}
          style={{ width: 70 }}
        />
        <button className="btn btn--primary" onClick={onSave} disabled={pending}>
          {pending ? 'Saving…' : 'Save & recompute BOM'}
        </button>
        {savedAt && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Saved {savedAt}</span>}
      </div>
    </div>
  );
}

type NumRow = { key: string; label: string; value: number };
function NumGrid({
  title, rows, onChange, suffix = '',
}: {
  title: string;
  rows: NumRow[];
  onChange: (key: string, v: number) => void;
  suffix?: string;
}) {
  return (
    <div style={{ marginTop: 16 }}>
      <h3 className="settings-h3" style={{ marginBottom: 8 }}>{title}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
        {rows.map((r) => (
          <label key={r.key} style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
            <span style={{ color: 'var(--text-muted)', marginBottom: 3 }}>{r.label}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="number" step="any" min={0}
                value={Number.isFinite(r.value) ? r.value : 0}
                onChange={(e) => onChange(r.key, Number(e.target.value))}
                style={{ flex: 1, padding: '4px 6px' }}
              />
              {suffix && <span style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{suffix}</span>}
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function MaterialsCostForm({ catalogs, plantId }: { catalogs: CostCatalog[]; plantId: string }) {
  const { draft, setDraft, savedAt, saveMut } = useCatalogEditor(catalogs, plantId);
  if (!draft) return <div style={{ padding: 16, color: 'var(--text-muted)' }}>No cost catalog found.</div>;

  const m = draft.materials ?? {};
  const cablePerFt: Record<string, number> = m.cable_per_ft ?? {};
  const dropCablePerFt: Record<string, number> = m.drop_cable_per_ft ?? {};
  const spliceClosure: Record<string, number> = m.splice_closure ?? {};
  const splitter: Record<string, number> = m.splitter ?? {};
  const vault: Record<string, number> = m.vault ?? {};

  const setMat = (patch: any) => setDraft((d) => d ? ({ ...d, materials: { ...(d.materials ?? {}), ...patch } }) : d);
  const setScalar = (key: string, v: number) => setMat({ [key]: v });
  const setNested = (group: string, key: string, v: number) =>
    setMat({ [group]: { ...(m[group] ?? {}), [key]: v } });

  return (
    <>
      <CatalogHeader
        draft={draft} savedAt={savedAt}
        onSave={() => saveMut.mutate(draft)}
        pending={saveMut.isPending}
        onChangeRegion={(v) => setDraft((d) => d ? { ...d, region_mult: v } : d)}
      />

      <NumGrid
        title="Cable $/ft by strand count"
        suffix="$/ft"
        rows={Object.keys(cablePerFt).sort((a, b) => Number(a) - Number(b))
          .map((k) => ({ key: k, label: `${k}-strand`, value: cablePerFt[k] }))}
        onChange={(k, v) => setNested('cable_per_ft', k, v)}
      />

      <NumGrid
        title="Drop cable $/ft by strand count"
        suffix="$/ft"
        rows={Object.keys(dropCablePerFt).sort((a, b) => Number(a) - Number(b))
          .map((k) => ({ key: k, label: `${k}-strand drop`, value: dropCablePerFt[k] }))}
        onChange={(k, v) => setNested('drop_cable_per_ft', k, v)}
      />

      <NumGrid
        title="Splice closures by capacity"
        suffix="$ each"
        rows={Object.keys(spliceClosure).sort((a, b) => Number(a) - Number(b))
          .map((k) => ({ key: k, label: `${k}-fiber closure`, value: spliceClosure[k] }))}
        onChange={(k, v) => setNested('splice_closure', k, v)}
      />

      <NumGrid
        title="Splitters"
        suffix="$ each"
        rows={Object.keys(splitter).map((k) => ({ key: k, label: k, value: splitter[k] }))}
        onChange={(k, v) => setNested('splitter', k, v)}
      />

      <NumGrid
        title="Vaults / handholes"
        suffix="$ each"
        rows={Object.keys(vault).map((k) => ({ key: k, label: `${k} vault`, value: vault[k] }))}
        onChange={(k, v) => setNested('vault', k, v)}
      />

      <NumGrid
        title="Hardware unit costs"
        suffix="$ each"
        rows={[
          { key: 'pedestal',       label: 'Pedestal',       value: Number(m.pedestal ?? 0) },
          { key: 'drop_terminal',  label: 'Drop terminal',  value: Number(m.drop_terminal ?? 0) },
          { key: 'ont',            label: 'ONT',            value: Number(m.ont ?? 0) },
          { key: 'olt_port',       label: 'OLT port',       value: Number(m.olt_port ?? 0) },
          { key: 'splice_tray_24', label: 'Splice tray 24', value: Number(m.splice_tray_24 ?? 0) },
        ]}
        onChange={(k, v) => setScalar(k, v)}
      />
    </>
  );
}

function LaborCostForm({ catalogs, plantId }: { catalogs: CostCatalog[]; plantId: string }) {
  const { draft, setDraft, savedAt, saveMut } = useCatalogEditor(catalogs, plantId);
  if (!draft) return <div style={{ padding: 16, color: 'var(--text-muted)' }}>No cost catalog found.</div>;

  const l = draft.labor ?? {};
  const perFt: Record<string, number> = l.per_ft ?? {};
  const perEach: Record<string, number> = l.per_each ?? {};
  const adders: Record<string, number> = l.percent_adders ?? {};

  const setLab = (patch: any) => setDraft((d) => d ? ({ ...d, labor: { ...(d.labor ?? {}), ...patch } }) : d);
  const setNested = (group: string, key: string, v: number) =>
    setLab({ [group]: { ...(l[group] ?? {}), [key]: v } });

  const prettyLabel = (k: string) =>
    k.replace(/_/g, ' ').replace(/\b\w/g, (s) => s.toUpperCase());

  return (
    <>
      <CatalogHeader
        draft={draft} savedAt={savedAt}
        onSave={() => saveMut.mutate(draft)}
        pending={saveMut.isPending}
        onChangeRegion={(v) => setDraft((d) => d ? { ...d, region_mult: v } : d)}
      />

      <NumGrid
        title="Labor — per foot"
        suffix="$/ft"
        rows={Object.keys(perFt).map((k) => ({ key: k, label: prettyLabel(k), value: perFt[k] }))}
        onChange={(k, v) => setNested('per_ft', k, v)}
      />

      <NumGrid
        title="Labor — per each"
        suffix="$ each"
        rows={Object.keys(perEach).map((k) => ({ key: k, label: prettyLabel(k), value: perEach[k] }))}
        onChange={(k, v) => setNested('per_each', k, v)}
      />

      <div style={{ marginTop: 20, padding: 12, background: 'var(--surface-2, #f8fafc)', border: '1px solid var(--border)', borderRadius: 8 }}>
        <h3 className="settings-h3" style={{ marginTop: 0, marginBottom: 6 }}>Project adders (% of subtotal)</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
          Applied on top of cable + hardware + labor. These are the levers that determine whether a BEAD project pencils.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
          {['engineering','permitting','project_mgmt','contingency'].map((k) => (
            <label key={k} style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
              <span style={{ color: 'var(--text-muted)', marginBottom: 3 }}>{prettyLabel(k)}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="number" step={0.005} min={0} max={1}
                  value={Number(adders[k] ?? 0)}
                  onChange={(e) => setNested('percent_adders', k, Number(e.target.value))}
                  style={{ flex: 1, padding: '4px 6px' }}
                />
                <span style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
                  {(Number(adders[k] ?? 0) * 100).toFixed(1)}%
                </span>
              </div>
            </label>
          ))}
        </div>
      </div>
    </>
  );
}

const DEFAULT_RADIOS: RadioEntry[] = [
  { id: 'siklu_eh8010fx', name: 'Siklu EtherHaul 8010FX (E-band)', type: 'ptp', freq_ghz: 80, max_throughput_mbps: 10000, max_range_km: 6.9, price_per_link_usd: 15000, max_tx_power_dbm: 18, antenna_gain_dbi: 43, rx_sensitivity_dbm: -50, fade_margin_db: 15, los_required: true, nlos_max_range_km: 0, enabled: true },
  { id: 'aviat_wtm4800_23g', name: 'Aviat WTM 4800 (23 GHz)', type: 'ptp', freq_ghz: 23, max_throughput_mbps: 3000, max_range_km: 12, price_per_link_usd: 12000, max_tx_power_dbm: 20, antenna_gain_dbi: 40, rx_sensitivity_dbm: -62, fade_margin_db: 12, los_required: true, nlos_max_range_km: 0, enabled: true },
  { id: 'siklu_eh710tx', name: 'Siklu EtherHaul 710TX (60 GHz)', type: 'ptp', freq_ghz: 60, max_throughput_mbps: 2000, max_range_km: 3.5, price_per_link_usd: 6000, max_tx_power_dbm: 22, antenna_gain_dbi: 37, rx_sensitivity_dbm: -55, fade_margin_db: 12, los_required: true, nlos_max_range_km: 0, enabled: true },
  { id: 'cambium_ptp820c_18g', name: 'Cambium PTP 820C (18 GHz)', type: 'ptp', freq_ghz: 18, max_throughput_mbps: 2000, max_range_km: 20, price_per_link_usd: 8000, max_tx_power_dbm: 22, antenna_gain_dbi: 38, rx_sensitivity_dbm: -65, fade_margin_db: 12, los_required: true, nlos_max_range_km: 0, enabled: true },
  { id: 'cambium_ptp820c_11g', name: 'Cambium PTP 820C (11 GHz)', type: 'ptp', freq_ghz: 11, max_throughput_mbps: 2000, max_range_km: 40, price_per_link_usd: 8000, max_tx_power_dbm: 25, antenna_gain_dbi: 34, rx_sensitivity_dbm: -70, fade_margin_db: 15, los_required: true, nlos_max_range_km: 0, enabled: true },
  { id: 'ubiquiti_af60lr', name: 'Ubiquiti airFiber 60 LR', type: 'ptp', freq_ghz: 60, max_throughput_mbps: 1880, max_range_km: 12, price_per_link_usd: 1000, max_tx_power_dbm: 22, antenna_gain_dbi: 38, rx_sensitivity_dbm: -56, fade_margin_db: 12, los_required: true, nlos_max_range_km: 0, enabled: true },
  { id: 'mimosa_b5x', name: 'Mimosa B5x', type: 'ptp', freq_ghz: 5.4, max_throughput_mbps: 1500, max_range_km: 100, price_per_link_usd: 1400, max_tx_power_dbm: 29, antenna_gain_dbi: 25, rx_sensitivity_dbm: -83, fade_margin_db: 12, los_required: true, nlos_max_range_km: 0, enabled: true },
  { id: 'cambium_ptp550', name: 'Cambium PTP 550', type: 'ptp', freq_ghz: 5.4, max_throughput_mbps: 1400, max_range_km: 80, price_per_link_usd: 2000, max_tx_power_dbm: 27, antenna_gain_dbi: 23, rx_sensitivity_dbm: -82, fade_margin_db: 12, los_required: true, nlos_max_range_km: 0, enabled: true },
  { id: 'ubiquiti_af5xhd', name: 'Ubiquiti airFiber 5XHD', type: 'ptp', freq_ghz: 5.4, max_throughput_mbps: 1340, max_range_km: 100, price_per_link_usd: 1100, max_tx_power_dbm: 29, antenna_gain_dbi: 16, rx_sensitivity_dbm: -85, fade_margin_db: 10, los_required: true, nlos_max_range_km: 0, enabled: true },
  { id: 'siklu_eh600', name: 'Siklu EtherHaul 600 (60 GHz)', type: 'ptp', freq_ghz: 60, max_throughput_mbps: 1000, max_range_km: 2.6, price_per_link_usd: 4000, max_tx_power_dbm: 20, antenna_gain_dbi: 35, rx_sensitivity_dbm: -60, fade_margin_db: 15, los_required: true, nlos_max_range_km: 0, enabled: true },
  { id: 'tarana_g1', name: 'Tarana G1 (5 GHz PTMP)', type: 'ptmp', freq_ghz: 5.4, max_throughput_mbps: 800, max_range_km: 30, price_per_link_usd: 4500, max_tx_power_dbm: 27, antenna_gain_dbi: 18, rx_sensitivity_dbm: -80, fade_margin_db: 10, los_required: false, nlos_max_range_km: 3, enabled: true },
  { id: 'cambium_ptp670', name: 'Cambium PTP 670', type: 'ptp', freq_ghz: 5.4, max_throughput_mbps: 450, max_range_km: 100, price_per_link_usd: 4000, max_tx_power_dbm: 27, antenna_gain_dbi: 23, rx_sensitivity_dbm: -87, fade_margin_db: 15, los_required: true, nlos_max_range_km: 0, enabled: true },
];

function EconomicsForm({ v, onChange }: { v: EconomicsSection; onChange: (p: Partial<EconomicsSection>) => void }) {
  return (<>
    <h3 className="settings-h3">Take rate baselines (two segments)</h3>
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
      Take rate differs sharply between unserved (BEAD-eligible) and already-served
      passings. BEAD areas have pent-up demand and LCSO pricing; served areas have
      switching friction with the incumbent. Sliders elsewhere in the app scale
      MRR linearly from these baselines per segment.
    </div>
    <Field label="Unserved / BEAD baseline take rate"
           hint="Decimal, 0.60 = 60%. Industry typical for BEAD-eligible BSLs: 50–70% (pent-up demand, LCSO pricing, often no competitor).">
      <input type="number" min={0.05} max={0.95} step={0.01}
        value={v.baseline_take_rate_unserved}
        onChange={(e) => onChange({ baseline_take_rate_unserved: Number(e.target.value) })} />
    </Field>
    <Field label="Already-served baseline take rate"
           hint="Decimal, 0.25 = 25%. Industry typical for bolt-on / private passings: 15–30% (incumbent lock-in, switching friction).">
      <input type="number" min={0.05} max={0.95} step={0.01}
        value={v.baseline_take_rate_served}
        onChange={(e) => onChange({ baseline_take_rate_served: Number(e.target.value) })} />
    </Field>

    <h3 className="settings-h3" style={{ marginTop: 18 }}>Default route density</h3>
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
      Estimated linear feet of fiber per BSL passing — used as a fallback when
      a project hasn't been auto-designed yet. Industry typical: 200–300 ft for
      rural FTTH (200 mainline + 50 drop). Higher in low-density rural; lower
      in MDU/urban builds.
    </div>
    <Field label="Feet of fiber per passing"
           hint="Drives the soil-aware labor delta on Project Economics when route geometry isn't known yet.">
      <input type="number" min={100} max={1000} step={10}
        value={v.ft_per_passing}
        onChange={(e) => onChange({ ft_per_passing: Number(e.target.value) })} />
    </Field>

    <h3 className="settings-h3" style={{ marginTop: 18 }}>Aerial mix slider default</h3>
    <Field label="Default aerial % when slider first appears"
           hint="Initial position of the aerial-mix slider on Builder + Project Economics. The Builder syncs this to the design's actual placement once analyze completes; this only matters for the first paint.">
      <input type="number" min={0} max={100} step={5}
        value={v.default_aerial_mix_pct}
        onChange={(e) => onChange({ default_aerial_mix_pct: Number(e.target.value) })} />
    </Field>
  </>);
}

function SplicingForm({ v, onChange }: { v: SplicingSection; onChange: (p: Partial<SplicingSection>) => void }) {
  return (<>
    <h3 className="settings-h3">Fiber splicing</h3>
    <Field label="Closure capacity (max strands per closure)"
           hint="Auto-design splits a feeder into multiple closures when total strands exceed this. 144 = standard mid-density.">
      <select value={v.closure_capacity} onChange={(e) => onChange({ closure_capacity: Number(e.target.value) as any })}>
        <option value={48}>48</option>
        <option value={96}>96</option>
        <option value={144}>144</option>
        <option value={216}>216</option>
        <option value={288}>288</option>
      </select>
    </Field>

    <Field label="Fusion splice loss (dB)"
           hint="Per-splice insertion loss. Industry typical: 0.10 dB.">
      <input type="number" min={0} max={1} step={0.01}
        value={v.fusion_loss_db}
        onChange={(e) => onChange({ fusion_loss_db: Number(e.target.value) })} />
    </Field>

    <Field label="Connector loss (dB)"
           hint="Per-connector insertion loss (SC/UPC, LC/UPC). Industry typical: 0.50 dB.">
      <input type="number" min={0} max={2} step={0.01}
        value={v.connector_loss_db}
        onChange={(e) => onChange({ connector_loss_db: Number(e.target.value) })} />
    </Field>

    <Field label="One splice tray per cable?"
           hint="True = each entering cable gets its own tray (cleaner, more closures). False = combine cables into shared trays (denser, lower material cost).">
      <select value={String(v.tray_per_cable)} onChange={(e) => onChange({ tray_per_cable: e.target.value === 'true' })}>
        <option value="true">Yes (one per cable)</option>
        <option value="false">No (shared trays)</option>
      </select>
    </Field>
  </>);
}

function CrossingsForm({ v, onChange }: { v: CrossingsSection; onChange: (p: Partial<CrossingsSection>) => void }) {
  return (<>
    <h3 className="settings-h3">Rail crossings</h3>
    <Field label="Default rail crossing method"
           hint="How the auto-designer crosses railroads when no permit data dictates otherwise. Bored is industry standard for new fiber to avoid track disruption.">
      <select value={v.rail_method} onChange={(e) => onChange({ rail_method: e.target.value as any })}>
        <option value="bored">Bored (HDD under the track)</option>
        <option value="open_cut">Open-cut (rare; needs railroad permit)</option>
      </select>
    </Field>

    <h3 className="settings-h3" style={{ marginTop: 18 }}>Water crossings</h3>
    <Field label="Water setback (ft)"
           hint="Minimum distance from any waterway centerline before fiber may parallel. Drives the corridor padding the routing engine applies near streams/rivers.">
      <input type="number" min={0} max={500} step={5}
        value={v.water_setback_ft}
        onChange={(e) => onChange({ water_setback_ft: Number(e.target.value) })} />
    </Field>

    <Field label="USACE §404 trigger"
           hint="When to flag a crossing for Clean Water Act §404 review. 'Any water crossing' is the safest interpretation; 'Navigable only' tracks the strict statutory definition; 'Manual' relies on per-crossing flagging.">
      <select value={v.usace_404_trigger} onChange={(e) => onChange({ usace_404_trigger: e.target.value as any })}>
        <option value="any_water_crossing">Any water crossing (conservative)</option>
        <option value="navigable_only">Navigable waters only (strict)</option>
        <option value="manual">Manual flagging</option>
      </select>
    </Field>
  </>);
}

function BackhaulForm({ v, onChange }: { v: BackhaulSection; onChange: (p: Partial<BackhaulSection>) => void }) {
  const [radios, setRadios] = useState<RadioEntry[]>(v.radios?.length ? v.radios : DEFAULT_RADIOS);
  const [showAddRadio, setShowAddRadio] = useState(false);

  const updateRadio = (idx: number, patch: Partial<RadioEntry>) => {
    const next = radios.map((r, i) => i === idx ? { ...r, ...patch } : r);
    setRadios(next);
    onChange({ radios: next });
  };
  const removeRadio = (idx: number) => {
    const next = radios.filter((_, i) => i !== idx);
    setRadios(next);
    onChange({ radios: next });
  };
  const addRadio = () => {
    const next: RadioEntry = {
      id: `custom_${Date.now()}`, name: 'New Radio', type: 'ptp', freq_ghz: 5.4,
      max_throughput_mbps: 1000, max_range_km: 20, price_per_link_usd: 5000,
      max_tx_power_dbm: 25, antenna_gain_dbi: 23, rx_sensitivity_dbm: -80,
      fade_margin_db: 12, los_required: true, nlos_max_range_km: 0, enabled: true,
    };
    const updated = [...radios, next];
    setRadios(updated);
    onChange({ radios: updated });
    setShowAddRadio(false);
  };

  return (<>
    <h3 className="settings-h3">Fiber vs wireless preference</h3>
    <Field label={`Fiber preference: ${v.fiber_preference_pct ?? 0}%`} hint="0% = pick the cheapest backhaul (wireless wins whenever viable). 100% = always build fiber (never use wireless unless there's no other choice). Intermediate values require wireless to span more miles to justify preferring it over fiber.">
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={v.fiber_preference_pct ?? 0}
        onChange={(e) => onChange({ fiber_preference_pct: Number(e.target.value) })}
        style={{ width: '100%', accentColor: '#0ea5e9' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
        <span>0% — Cost-optimal</span>
        <span>50% — Balanced</span>
        <span>100% — All fiber</span>
      </div>
    </Field>

    <h3 className="settings-h3">Wireless radio selection</h3>
    <Field label="Optimize for" hint="How the system picks radios when auto-analyzing wireless PTP options for expansion zones.">
      <select value={v.radio_priority} onChange={(e) => onChange({ radio_priority: e.target.value as any })}>
        <option value="throughput">Highest throughput (fastest link wins)</option>
        <option value="cost">Lowest cost (cheapest viable radio wins)</option>
        <option value="link_margin">Best link margin (most reliable link wins)</option>
      </select>
    </Field>
    <Field label="Minimum acceptable throughput (Mbps)" hint="Links below this are rejected even if LoS is clear.">
      <input type="number" min={10} max={10000} step={10} value={v.min_throughput_mbps} onChange={(e) => onChange({ min_throughput_mbps: Number(e.target.value) })} />
    </Field>
    <Field label="Max relay hops" hint="Maximum tower-to-tower hops in a wireless relay chain. More hops = more coverage but more latency + cost.">
      <input type="number" min={1} max={10} value={v.max_hops} onChange={(e) => onChange({ max_hops: Number(e.target.value) })} />
    </Field>

    <h3 className="settings-h3" style={{ marginTop: 20 }}>Tower rental</h3>
    <Field label="Tower rental ($/ft height/month)" hint="Monthly cost per foot of tower height. Two tower sites per hop. E.g., 100 ft tower = $100/mo.">
      <input type="number" min={0} max={10} step={0.25} value={v.tower_rental_per_ft_month ?? 1} onChange={(e) => onChange({ tower_rental_per_ft_month: Number(e.target.value) })} />
    </Field>

    <h3 className="settings-h3" style={{ marginTop: 20 }}>Lease PTP fiber circuit</h3>
    <Field label="Monthly rate ($/mo)" hint="Typical cost for a leased lit 10G PTP circuit from a carrier (Zayo, Lumen, Windstream, local telco). Flat rate regardless of distance.">
      <input type="number" min={100} max={50000} step={50} value={v.lease_ptp_monthly} onChange={(e) => onChange({ lease_ptp_monthly: Number(e.target.value) })} />
    </Field>
    <h3 className="settings-h3" style={{ marginTop: 20 }}>Radio catalog</h3>
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
      Radios used for wireless PTP backhaul analysis. Edit prices to match your vendor quotes. Disable radios you don't stock. Add custom entries for any radio not listed.
    </div>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
            <th style={{ padding: '4px 6px' }}>On</th>
            <th style={{ padding: '4px 6px' }}>Name</th>
            <th style={{ padding: '4px 6px' }}>Type</th>
            <th style={{ padding: '4px 6px' }}>Freq</th>
            <th style={{ padding: '4px 6px' }}>Throughput</th>
            <th style={{ padding: '4px 6px' }}>Range</th>
            <th style={{ padding: '4px 6px' }}>Price/link</th>
            <th style={{ padding: '4px 6px' }}></th>
          </tr>
        </thead>
        <tbody>
          {radios.map((r, i) => (
            <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', opacity: r.enabled ? 1 : 0.5 }}>
              <td style={{ padding: '4px 6px' }}>
                <input type="checkbox" checked={r.enabled} onChange={(e) => updateRadio(i, { enabled: e.target.checked })} />
              </td>
              <td style={{ padding: '4px 6px' }}>
                <input type="text" value={r.name} onChange={(e) => updateRadio(i, { name: e.target.value })} style={{ width: '100%', padding: '2px 4px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 3 }} />
              </td>
              <td style={{ padding: '4px 6px', fontSize: 10 }}>{r.type}</td>
              <td style={{ padding: '4px 6px' }}>
                <input type="number" value={r.freq_ghz} onChange={(e) => updateRadio(i, { freq_ghz: Number(e.target.value) })} style={{ width: 50, padding: '2px', fontSize: 11 }} /> GHz
              </td>
              <td style={{ padding: '4px 6px' }}>
                <input type="number" value={r.max_throughput_mbps} onChange={(e) => updateRadio(i, { max_throughput_mbps: Number(e.target.value) })} style={{ width: 60, padding: '2px', fontSize: 11 }} /> Mbps
              </td>
              <td style={{ padding: '4px 6px' }}>
                <input type="number" value={r.max_range_km} step={0.1} onChange={(e) => updateRadio(i, { max_range_km: Number(e.target.value) })} style={{ width: 50, padding: '2px', fontSize: 11 }} /> km
              </td>
              <td style={{ padding: '4px 6px' }}>
                $<input type="number" value={r.price_per_link_usd} step={100} onChange={(e) => updateRadio(i, { price_per_link_usd: Number(e.target.value) })} style={{ width: 65, padding: '2px', fontSize: 11 }} />
              </td>
              <td style={{ padding: '4px 6px' }}>
                <button onClick={() => removeRadio(i)} style={{ fontSize: 10, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }} title="Remove">✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <button className="btn" onClick={addRadio} style={{ marginTop: 8, fontSize: 11 }}>+ Add radio</button>

    <div style={{ marginTop: 12, padding: 10, background: '#f0f9ff', border: '1px solid #93c5fd', borderRadius: 6, fontSize: 11, color: 'var(--text-muted)' }}>
      <b style={{ color: '#1e40af' }}>Tip:</b> Click the column headers to understand each field. The system tries radios sorted by your <b>priority</b> (throughput/cost/margin) and picks the first one that has LoS + enough range. Disabled radios are skipped entirely. Advanced RF specs (Tx power, antenna gain, Rx sensitivity, fade margin) can be edited by clicking a radio name — expand row coming soon.
    </div>
  </>);
}

// Drops settings — max drop length is the most demand-relevant field;
// the orphan-rescue path uses a separate hardcoded 1500 ft because BEAD
// orphans are remote and need longer drops than typical builds.
function DropsForm({ v, onChange }: { v: DropsSection; onChange: (p: Partial<DropsSection>) => void }) {
  return (<>
    <h3 className="settings-h3">Drop length & feasibility</h3>
    <Field label="Max drop length (ft)" hint="Drops longer than this are infeasible. Buildings beyond this distance from a feasible cable path will fail analysis. Industry-typical: 300–500 ft for new builds, longer for rural. (Note: the BEAD orphan-rescue path uses a separate hardcoded 1500 ft.)">
      <input type="number" min={50} max={3000} step={10}
        value={v.max_drop_ft ?? 500}
        onChange={(e) => onChange({ max_drop_ft: Number(e.target.value) })} />
    </Field>

    <h3 className="settings-h3">Placement</h3>
    <Field label="Default drop placement" hint="The default physical method when not running aerial. Used together with the aerial/buried mix slider below.">
      <select value={v.placement ?? 'buried'} onChange={(e) => onChange({ placement: e.target.value as DropsSection['placement'] })}>
        <option value="aerial">Aerial</option>
        <option value="buried">Buried</option>
        <option value="conduit">Conduit (existing)</option>
        <option value="direct_bury">Direct bury</option>
        <option value="micro_trench">Micro trench</option>
      </select>
    </Field>
    <Field label={`Aerial / buried mix · ${(v.aerial_pct ?? 0)}% aerial \u00b7 ${100 - (v.aerial_pct ?? 0)}% buried`}
      hint="Slide to set what fraction of drops run aerially vs. buried. Cost rollup blends aerial-rate $/ft with buried-rate $/ft accordingly. Aerial = cheaper + faster, requires pole attachment. Buried = costlier, cleaner.">
      <input type="range" min={0} max={100} step={5}
        value={v.aerial_pct ?? 0}
        onChange={(e) => onChange({ aerial_pct: Number(e.target.value) })}
        style={{ width: '100%' }} />
    </Field>

    <h3 className="settings-h3">Multi-dwelling units (MDU)</h3>
    <Field label="MDU treatment" hint="How an MDU (apartment / condo) gets cabled.">
      <select value={v.mdu_treatment ?? 'single_drop_to_bef'} onChange={(e) => onChange({ mdu_treatment: e.target.value as DropsSection['mdu_treatment'] })}>
        <option value="single_drop_to_bef">Single drop to building entry facility (most common)</option>
        <option value="one_drop_per_unit">Drop per unit (homerun)</option>
        <option value="n/a">Not applicable</option>
      </select>
    </Field>

    <h3 className="settings-h3">Generation</h3>
    <Field label="Drops per building (default)" hint="Most builds = 1 (single drop into a NID/ONT inside)">
      <input type="number" min={1} max={4} step={1}
        value={v.drops_per_building ?? 1}
        onChange={(e) => onChange({ drops_per_building: Number(e.target.value) })} />
    </Field>
    <Field label="Auto-generate drops during auto-design" hint="When enabled, the analyzer places drops automatically. When disabled, you place drops manually post-analysis.">
      <input type="checkbox" checked={v.generate_in_auto_design ?? true}
        onChange={(e) => onChange({ generate_in_auto_design: e.target.checked })} />
    </Field>
    {(v.generate_in_auto_design ?? true) && (
      <Field label="Drop generation policy" hint="'Every passing' builds drops to all addressable buildings (pre-build model — bigger BoM, faster service activations). 'Take rate only' generates drops sized to take_rate% of passings (realistic deployment — smaller BoM, drops added post-sale). Take rate value is configured per analysis run (default 40%).">
        <select value={v.generation_mode ?? 'every_passing'}
          onChange={(e) => onChange({ generation_mode: e.target.value as DropsSection['generation_mode'] })}>
          <option value="every_passing">Every passing (pre-build all drops)</option>
          <option value="take_rate">Take rate only (drops only for subscribers)</option>
        </select>
      </Field>
    )}
  </>);
}

